import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Public page (no login) that explains how to delete a SocraQuest account.
// Linked from Google Play (Data safety > "Delete account URL") and the App Store.
const EMAIL = 'privacy@socraquest.sk';

const CONTENT = {
  en: {
    back: 'Back',
    title: 'Delete your SocraQuest account',
    intro: 'SocraQuest (Android, iPhone and https://socraquest.sk) lets you delete your account and your personal data at any time. Choose one of the options below.',
    steps: [
      ['In the app or on the website', [
        'Open the SocraQuest app, or log in at https://socraquest.sk.',
        'Go to Profile.',
        'Scroll to "Delete account" and tap "Delete my account".',
        'Type DELETE and tap "Delete permanently". Your account is deleted immediately.',
      ]],
      ['By e-mail (if you cannot log in)', [
        `Send an e-mail to ${EMAIL} from the e-mail address of your SocraQuest account.`,
        'Subject: "Delete my SocraQuest account".',
        'We delete the account within 30 days and confirm it by e-mail.',
      ]],
    ],
    deletedTitle: 'What is deleted',
    deleted: [
      'Your account: e-mail address, nickname, name, password and profile',
      'Your assessment attempts, results, XP, badges, streaks and rankings',
      'Your group memberships (groups you own are handed to another member or deleted if empty)',
      'Devices and push notification tokens, notification settings',
      'Unpaid rewards and open payout requests, and your bank details (IBAN)',
    ],
    keptTitle: 'What is kept',
    kept: [
      'Records of rewards that were already paid out are kept for accounting and tax purposes as long as the law requires (without your bank details).',
      'Questions you reported as wrong are kept without your name.',
    ],
    more: 'More details in the',
    privacy: 'Privacy Policy',
    contact: 'Questions',
    footer: '© 2026 SocraQuest. All rights reserved.',
  },
  sk: {
    back: 'Späť',
    title: 'Zrušenie účtu SocraQuest',
    intro: 'V SocraQuest (Android, iPhone a https://socraquest.sk) môžete svoj účet a osobné údaje kedykoľvek zmazať. Vyberte si jednu z možností nižšie.',
    steps: [
      ['V aplikácii alebo na webe', [
        'Otvorte aplikáciu SocraQuest alebo sa prihláste na https://socraquest.sk.',
        'Prejdite do časti Profil.',
        'V časti „Zrušenie účtu“ ťuknite na „Zmazať môj účet“.',
        'Napíšte DELETE a ťuknite na „Zmazať natrvalo“. Účet sa zmaže okamžite.',
      ]],
      ['E-mailom (ak sa nemôžete prihlásiť)', [
        `Pošlite e-mail na ${EMAIL} z e-mailovej adresy, ktorú používate v SocraQuest.`,
        'Predmet: „Zmazať môj účet SocraQuest“.',
        'Účet zmažeme do 30 dní a potvrdíme Vám to e-mailom.',
      ]],
    ],
    deletedTitle: 'Čo sa zmaže',
    deleted: [
      'Váš účet: e-mailová adresa, prezývka, meno, heslo a profil',
      'Vaše pokusy, výsledky, XP, odznaky, série a umiestnenia v rebríčkoch',
      'Členstvo v skupinách (Vaše skupiny prejdú na iného člena alebo sa zmažú, ak sú prázdne)',
      'Zariadenia a tokeny pre notifikácie, nastavenia notifikácií',
      'Nevyplatené odmeny, otvorené žiadosti o výplatu a Vaše bankové údaje (IBAN)',
    ],
    keptTitle: 'Čo sa uchová',
    kept: [
      'Záznamy o už vyplatených odmenách uchovávame na účtovné a daňové účely, kým to vyžaduje zákon (bez Vašich bankových údajov).',
      'Otázky, ktoré ste nahlásili ako chybné, zostanú uložené bez Vášho mena.',
    ],
    more: 'Viac informácií nájdete v dokumente',
    privacy: 'Zásady ochrany osobných údajov',
    contact: 'Otázky',
    footer: '© 2026 SocraQuest. Všetky práva vyhradené.',
  },
};

export default function DeleteAccountPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').toLowerCase().startsWith('sk') ? 'sk' : 'en';
  const c = CONTENT[lang];

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button onClick={goBack} className="flex items-center text-teal-600 hover:text-teal-700 mr-4" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-1" />
            {c.back}
          </button>
          <h1 className="text-xl font-bold text-slate-800">SocraQuest</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="delete-account-page">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-4">{c.title}</h1>
          <p className="text-slate-600 mb-8">{c.intro}</p>

          {c.steps.map(([heading, items]) => (
            <section key={heading} className="mb-8">
              <h2 className="text-xl font-semibold text-slate-800 mb-4">{heading}</h2>
              <ol className="list-decimal list-inside text-slate-600 space-y-2">
                {items.map((item) => <li key={item}>{item}</li>)}
              </ol>
            </section>
          ))}

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">{c.deletedTitle}</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              {c.deleted.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">{c.keptTitle}</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2">
              {c.kept.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 text-slate-600">
            <p>
              {c.more}{' '}
              <Link to="/privacy-policy" className="text-teal-700 underline">{c.privacy}</Link>.
            </p>
            <p className="mt-2">{c.contact}: <a href={`mailto:${EMAIL}`} className="text-teal-700 underline">{EMAIL}</a></p>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-slate-200 mt-8">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          {c.footer}
        </div>
      </footer>
    </div>
  );
}
